import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { Organization } from '../../../types/organization';
import type { 
  BusinessUnit,
  Branch, 
  PointOfSale, 
  OrganizationHierarchy,
  BusinessUnitFilterDto,
  BusinessUnitAggregateDto,
  BusinessUnitHierarchyDto,
  BusinessUnitQuickStats,
  CreateBusinessUnitDto,
  UpdateBusinessUnitDto,
  PaginatedBusinessUnitsResponse,
  Company
} from '../../../types/business-unit';

/**
 * Transforme la réponse de l'API /business-units/hierarchy
 * L'API retourne un tableau plat de business units: { data: [...] }
 * On doit le convertir en structure OrganizationHierarchy (company, branches, pointsOfSale)
 */
function transformHierarchyResponseToOrganizationHierarchy(
  apiResponse: any,
  organizationName?: string,
  organizationId?: string
): OrganizationHierarchy {
  const branches: Branch[] = [];
  const pointsOfSale: PointOfSale[] = [];
  
  // L'API peut retourner un tableau directement ou un objet avec data
  const items: any[] = Array.isArray(apiResponse) 
    ? apiResponse 
    : (apiResponse?.data || apiResponse?.items || []);
  
  // Fonction pour mapper un item API vers Branch ou PointOfSale
  function processItem(item: any, parentBranchId?: string) {
    if (item.type === 'branch') {
      const branch: Branch = {
        id: item.id,
        code: item.code,
        name: item.name,
        type: 'branch',
        status: item.status || 'active',
        companyId: item.companyId || organizationId || '',
        parentId: item.parentId,
        hierarchyLevel: item.hierarchyLevel ?? item.level ?? 1,
        hierarchyPath: item.hierarchyPath,
        address: item.address,
        city: item.city,
        province: item.province,
        country: item.country || 'RDC',
        phone: item.phone,
        email: item.email,
        manager: item.manager,
        managerId: item.managerId,
        currency: item.currency || 'CDF',
        timezone: item.timezone || 'Africa/Kinshasa',
        settings: item.settings,
        metadata: item.metadata,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
        createdBy: item.createdBy,
        updatedBy: item.updatedBy
      };
      branches.push(branch);
      
      // Traiter les enfants (POS) de cette branche
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach((child: any) => processItem(child, item.id));
      }
    } else if (item.type === 'pos') {
      const pos: PointOfSale = {
        id: item.id,
        code: item.code,
        name: item.name,
        type: 'pos',
        status: item.status || 'active',
        companyId: item.companyId || organizationId || '',
        parentId: item.parentId,
        branchId: parentBranchId || item.branchId || item.parentId,
        hierarchyLevel: item.hierarchyLevel ?? item.level ?? 2,
        hierarchyPath: item.hierarchyPath,
        address: item.address,
        city: item.city,
        province: item.province,
        country: item.country || 'RDC',
        phone: item.phone,
        email: item.email,
        manager: item.manager,
        managerId: item.managerId,
        posType: item.posType || 'shop',
        currency: item.currency || 'CDF',
        timezone: item.timezone || 'Africa/Kinshasa',
        settings: item.settings,
        metadata: item.metadata,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
        createdBy: item.createdBy,
        updatedBy: item.updatedBy
      };
      pointsOfSale.push(pos);
      
      // Un POS peut aussi avoir des enfants (rare mais possible)
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach((child: any) => processItem(child, item.id));
      }
    } else if (item.type === 'company') {
      // Si on reçoit une company dans le tableau, traiter ses enfants
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach((child: any) => processItem(child));
      }
    }
  }
  
  // Traiter tous les éléments du tableau
  items.forEach(item => processItem(item));
  
  // Construire un objet company par défaut
  // (L'info de la company vient normalement de /auth/me, pas de cet endpoint)
  const company: Company = {
    id: organizationId || 'company-placeholder',
    code: 'COMPANY',
    name: organizationName || 'Mon Entreprise',
    type: 'company',
    status: 'active',
    companyId: organizationId || 'company-placeholder',
    hierarchyLevel: 0,
    hierarchyPath: `/${organizationId || 'company-placeholder'}`,
    country: 'RDC',
    currency: 'CDF',
    timezone: 'Africa/Kinshasa',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  console.log(`📊 Hiérarchie chargée: ${branches.length} succursale(s), ${pointsOfSale.length} POS`);
  
  return {
    company,
    branches,
    pointsOfSale
  };
}

export interface FiscalSettings {
  vatRegistered: boolean;
  vatNumber?: string;
  vatRate: number;
  taxPaymentFrequency: string;
  fiscalYearStart: {
    month: number;
    day: number;
  };
  taxationSystem: string;
}

export interface BankDetails {
  id: string;
  bankName: string;
  accountNumber: string;
  iban?: string;
  swift?: string;
  currency: string;
  isPrimary: boolean;
}

// Types pour la création/modification de succursales et points de vente (legacy)
export interface CreateBranchRequest {
  name: string;
  city?: string;
  address?: string;
  phone?: string;
  email?: string;
  managerId?: string;
}

export interface UpdateBranchRequest extends Partial<CreateBranchRequest> {
  isActive?: boolean;
}

export interface CreatePosRequest {
  name: string;
  branchId?: string; // Optionnel - peut être directement sous l'entreprise
  address?: string;
  phone?: string;
  posType?: 'shop' | 'warehouse' | 'kiosk' | 'online' | 'other';
  managerId?: string;
}

export interface UpdatePosRequest extends Partial<CreatePosRequest> {
  isActive?: boolean;
}

// Paramètres pour les données agrégées
export interface AggregateParams {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  breakdown?: boolean;
}

export const organizationApi = {
  // ============================================
  // ORGANISATION GÉNÉRALE
  // ============================================
  
  // Récupérer les informations de l'organisation
  getOrganization: (): Promise<ApiResponse<Organization>> =>
    ApiService.get<Organization>('/organization'),

  // Mettre à jour les informations de l'organisation
  updateOrganization: (data: Partial<Organization>): Promise<ApiResponse<Organization>> =>
    ApiService.put<Organization>('/organization', data),

  // Mettre à jour le logo de l'organisation
  updateLogo: (logoUrl: string): Promise<ApiResponse<{ logo: string }>> =>
    ApiService.post<{ logo: string }>('/organization/logo', { logo: logoUrl }),

  // ============================================
  // PARAMÈTRES FISCAUX
  // ============================================
  
  // Récupérer les paramètres fiscaux
  getFiscalSettings: (): Promise<ApiResponse<FiscalSettings>> =>
    ApiService.get<FiscalSettings>('/organization/fiscal-settings'),

  // Mettre à jour les paramètres fiscaux
  updateFiscalSettings: (data: Partial<FiscalSettings>): Promise<ApiResponse<FiscalSettings>> =>
    ApiService.put<FiscalSettings>('/organization/fiscal-settings', data),

  // ============================================
  // COORDONNÉES BANCAIRES
  // ============================================
  
  // Récupérer les coordonnées bancaires
  getBankDetails: (): Promise<ApiResponse<BankDetails[]>> =>
    ApiService.get<BankDetails[]>('/organization/bank-details'),

  // Ajouter des coordonnées bancaires
  addBankDetails: (data: Omit<BankDetails, 'id'>): Promise<ApiResponse<BankDetails>> =>
    ApiService.post<BankDetails>('/organization/bank-details', data),

  // Mettre à jour des coordonnées bancaires
  updateBankDetails: (id: string, data: Partial<BankDetails>): Promise<ApiResponse<BankDetails>> =>
    ApiService.put<BankDetails>(`/organization/bank-details/${id}`, data),

  // Supprimer des coordonnées bancaires
  deleteBankDetails: (id: string): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/organization/bank-details/${id}`),

  // ============================================
  // HIÉRARCHIE ORGANISATIONNELLE
  // ============================================
  
  /**
   * Récupérer la hiérarchie complète (entreprise, succursales, points de vente)
   * Appelle /business-units/hierarchy et transforme le tableau en structure OrganizationHierarchy
   * @param organizationName - Nom de l'organisation (pour affichage)
   * @param organizationId - ID de l'organisation (pour lier les business units)
   */
  getHierarchy: async (organizationName?: string, organizationId?: string): Promise<ApiResponse<OrganizationHierarchy>> => {
    try {
      // Endpoint officiel - retourne un tableau de business units
      const response = await ApiService.get<any>('/business-units/hierarchy');
      
      if (response.success && response.data) {
        // Vérifier si c'est déjà une structure OrganizationHierarchy (company, branches, pointsOfSale)
        if (response.data.company && response.data.branches && response.data.pointsOfSale) {
          return response as ApiResponse<OrganizationHierarchy>;
        }
        
        // Sinon, c'est un tableau ou une structure à transformer
        const hierarchy = transformHierarchyResponseToOrganizationHierarchy(
          response.data, 
          organizationName, 
          organizationId
        );
        return { success: true, data: hierarchy };
      }
      
      // Fallback vers l'ancien endpoint si le nouveau échoue
      const legacyResponse = await ApiService.get<OrganizationHierarchy>('/organization/hierarchy');
      return legacyResponse;
    } catch (error) {
      console.error('Erreur lors de la récupération de la hiérarchie:', error);
      // Fallback en cas d'erreur
      return ApiService.get<OrganizationHierarchy>('/organization/hierarchy');
    }
  },

  // ============================================
  // SUCCURSALES
  // ============================================
  
  // Récupérer toutes les succursales
  getBranches: (): Promise<ApiResponse<Branch[]>> =>
    ApiService.get<Branch[]>('/organization/branches'),

  // Récupérer une succursale par ID
  getBranchById: (branchId: string): Promise<ApiResponse<Branch>> =>
    ApiService.get<Branch>(`/organization/branches/${branchId}`),

  // Créer une nouvelle succursale
  createBranch: (data: CreateBranchRequest): Promise<ApiResponse<Branch>> =>
    ApiService.post<Branch>('/organization/branches', data),

  // Mettre à jour une succursale
  updateBranch: (branchId: string, data: UpdateBranchRequest): Promise<ApiResponse<Branch>> =>
    ApiService.put<Branch>(`/organization/branches/${branchId}`, data),

  // Supprimer une succursale
  deleteBranch: (branchId: string): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/organization/branches/${branchId}`),

  // Activer/Désactiver une succursale
  toggleBranchStatus: (branchId: string): Promise<ApiResponse<Branch>> =>
    ApiService.patch<Branch>(`/organization/branches/${branchId}/toggle-status`, {}),

  // ============================================
  // POINTS DE VENTE
  // ============================================
  
  // Récupérer tous les points de vente
  getPointsOfSale: (params?: { branchId?: string }): Promise<ApiResponse<PointOfSale[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.branchId) searchParams.append('branchId', params.branchId);
    const query = searchParams.toString();
    return ApiService.get<PointOfSale[]>(`/organization/pos${query ? `?${query}` : ''}`);
  },

  // Récupérer un point de vente par ID
  getPosById: (posId: string): Promise<ApiResponse<PointOfSale>> =>
    ApiService.get<PointOfSale>(`/organization/pos/${posId}`),

  // Créer un nouveau point de vente
  createPos: (data: CreatePosRequest): Promise<ApiResponse<PointOfSale>> =>
    ApiService.post<PointOfSale>('/organization/pos', data),

  // Mettre à jour un point de vente
  updatePos: (posId: string, data: UpdatePosRequest): Promise<ApiResponse<PointOfSale>> =>
    ApiService.put<PointOfSale>(`/organization/pos/${posId}`, data),

  // Supprimer un point de vente
  deletePos: (posId: string): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/organization/pos/${posId}`),

  // Activer/Désactiver un point de vente
  togglePosStatus: (posId: string): Promise<ApiResponse<PointOfSale>> =>
    ApiService.patch<PointOfSale>(`/organization/pos/${posId}/toggle-status`, {}),

  // ============================================
  // BUSINESS UNITS (API UNIFIÉE)
  // Nouveaux endpoints selon la documentation API
  // ============================================

  /**
   * Créer une nouvelle Business Unit (succursale ou point de vente)
   * POST /business-units
   * Rôles autorisés: admin, super_admin
   */
  createBusinessUnit: (data: CreateBusinessUnitDto): Promise<ApiResponse<BusinessUnit>> =>
    ApiService.post<BusinessUnit>('/business-units', data),

  /**
   * Lister les Business Units avec filtres et pagination
   * GET /business-units
   */
  getBusinessUnits: (params?: BusinessUnitFilterDto): Promise<ApiResponse<PaginatedBusinessUnitsResponse>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.type) searchParams.append('type', params.type);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.parentId) searchParams.append('parentId', params.parentId);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.city) searchParams.append('city', params.city);
    if (params?.province) searchParams.append('province', params.province);
    if (params?.includeChildren !== undefined) searchParams.append('includeChildren', params.includeChildren.toString());
    const query = searchParams.toString();
    return ApiService.get<PaginatedBusinessUnitsResponse>(`/business-units${query ? `?${query}` : ''}`);
  },

  /**
   * Obtenir la hiérarchie complète des Business Units
   * GET /business-units/hierarchy
   */
  getBusinessUnitsHierarchy: (): Promise<ApiResponse<BusinessUnitHierarchyDto>> =>
    ApiService.get<BusinessUnitHierarchyDto>('/business-units/hierarchy'),

  /**
   * Obtenir une Business Unit par son ID
   * GET /business-units/:id
   */
  getBusinessUnitById: (id: string): Promise<ApiResponse<BusinessUnit>> =>
    ApiService.get<BusinessUnit>(`/business-units/${id}`),

  /**
   * Obtenir les enfants directs d'une Business Unit
   * GET /business-units/:id/children
   */
  getBusinessUnitChildren: (id: string): Promise<ApiResponse<BusinessUnit[]>> =>
    ApiService.get<BusinessUnit[]>(`/business-units/${id}/children`),

  /**
   * Obtenir tous les descendants d'une Business Unit (récursif)
   * GET /business-units/:id/descendants
   */
  getBusinessUnitDescendants: (id: string): Promise<ApiResponse<BusinessUnit[]>> =>
    ApiService.get<BusinessUnit[]>(`/business-units/${id}/descendants`),

  /**
   * Obtenir les données agrégées d'une Business Unit
   * GET /business-units/:id/aggregate
   */
  getBusinessUnitAggregate: (id: string, params: AggregateParams): Promise<ApiResponse<BusinessUnitAggregateDto>> => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    if (params.breakdown !== undefined) searchParams.append('breakdown', params.breakdown.toString());
    return ApiService.get<BusinessUnitAggregateDto>(`/business-units/${id}/aggregate?${searchParams.toString()}`);
  },

  /**
   * Obtenir les statistiques rapides d'une Business Unit
   * GET /business-units/:id/stats
   */
  getBusinessUnitStats: (id: string): Promise<ApiResponse<BusinessUnitQuickStats>> =>
    ApiService.get<BusinessUnitQuickStats>(`/business-units/${id}/stats`),

  /**
   * Mettre à jour une Business Unit
   * PUT /business-units/:id
   * Rôles autorisés: admin, super_admin
   */
  updateBusinessUnit: (id: string, data: UpdateBusinessUnitDto): Promise<ApiResponse<BusinessUnit>> =>
    ApiService.put<BusinessUnit>(`/business-units/${id}`, data),

  /**
   * Désactiver une Business Unit et tous ses descendants
   * POST /business-units/:id/deactivate
   * Rôles autorisés: admin, super_admin
   */
  deactivateBusinessUnit: (id: string): Promise<ApiResponse<BusinessUnit>> =>
    ApiService.post<BusinessUnit>(`/business-units/${id}/deactivate`, {}),

  /**
   * Supprimer définitivement une Business Unit
   * DELETE /business-units/:id
   * Rôles autorisés: super_admin
   * Note: La Business Unit ne doit pas avoir d'enfants actifs
   */
  deleteBusinessUnit: (id: string): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/business-units/${id}`)
};
